import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

import { useSession } from '@/stores/session';
import { getRealtimeToken, getSubscribeToken } from './api';
import { RealtimeManager } from './manager';
import { RealtimeContext, type RealtimeContextValue } from './realtimeContext';
import type {
  LiveHandler,
  PresenceHandlers,
  PublicationHandler,
  RealtimeStatus,
  Unsubscribe,
} from './types';

/**
 * Owns the app's single realtime connection (FE-ADR-066).
 *
 * Connects only while the caller is signed in **to an org** — the connection
 * token is per membership. `activeOrgSlug` is an effect dependency on purpose:
 * switching org tears the socket down and builds a new one, so a connection
 * authorized for one org never carries another org's traffic (F1). Signing out
 * clears the session, which stops it.
 *
 * With `VITE_CENTRIFUGO_URL` unset the provider mounts, reports `disabled`, and
 * does nothing else — no client, no requests, no listeners.
 */
export function RealtimeProvider({ children }: { children: ReactNode }) {
  const url = import.meta.env.VITE_CENTRIFUGO_URL ?? '';
  const isAuthenticated = useSession((s) => s.isAuthenticated);
  const activeOrgSlug = useSession((s) => s.activeOrgSlug);

  const [status, setStatus] = useState<RealtimeStatus>(url ? 'disconnected' : 'disabled');
  const [userChannel, setUserChannel] = useState<string | null>(null);
  const [presenceChannel, setPresenceChannel] = useState<string | null>(null);

  // One manager for the app's lifetime — the socket inside it starts and
  // stops. `url` is build-time config, so the lazy initializer runs once.
  const [manager] = useState<RealtimeManager | null>(() =>
    url
      ? new RealtimeManager({
          url,
          getConnectionToken: async () => {
            const dto = await getRealtimeToken();
            return {
              token: dto.token,
              userChannel: dto.user_channel,
              presenceChannel: dto.presence_channel,
            };
          },
          getSubscriptionToken: async (channel) => (await getSubscribeToken(channel)).token,
          onStatus: setStatus,
          onUserChannel: setUserChannel,
          onPresenceChannel: setPresenceChannel,
        })
      : null,
  );

  useEffect(() => {
    if (!manager || !isAuthenticated || !activeOrgSlug) return;
    manager.start();
    return () => manager.stop();
  }, [manager, isAuthenticated, activeOrgSlug]);

  // Stable identity: `useRealtimeChannel` lists it as an effect dependency.
  const subscribe = useCallback(
    <T,>(
      channel: string,
      handler: PublicationHandler<T>,
      onLive?: LiveHandler,
      presence?: PresenceHandlers,
    ): Unsubscribe => manager?.subscribe(channel, handler, onLive, presence) ?? (() => {}),
    [manager],
  );

  const value = useMemo<RealtimeContextValue>(
    () => ({ status, userChannel, presenceChannel, subscribe }),
    [status, userChannel, presenceChannel, subscribe],
  );

  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>;
}
